import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

// Line-by-line masked reveal
const revealLine = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.9 + i * 0.12 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

// Ornamental gold divider with a lotus dot
const Divider = () => (
  <motion.div
    data-testid="cs-divider"
    className="flex items-center justify-center gap-3 sm:gap-4"
    variants={fadeUp}
    custom={1.7}
  >
    <motion.span
      className="block h-px bg-gradient-to-r from-transparent to-[var(--gold)]"
      initial={{ width: 0 }}
      animate={{ width: "clamp(40px, 9vw, 120px)" }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 1.8 }}
    />
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path
        d="M12 3c1.6 2.2 1.6 5 0 7-1.6-2-1.6-4.8 0-7Zm0 18c-1.6-2.2-1.6-5 0-7 1.6 2 1.6 4.8 0 7ZM3 12c2.2-1.6 5-1.6 7 0-2 1.6-4.8 1.6-7 0Zm18 0c-2.2 1.6-5 1.6-7 0 2-1.6 4.8-1.6 7 0Z"
        stroke="var(--gold)"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="1.5" fill="var(--gold)" />
    </svg>
    <motion.span
      className="block h-px bg-gradient-to-l from-transparent to-[var(--gold)]"
      initial={{ width: 0 }}
      animate={{ width: "clamp(40px, 9vw, 120px)" }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 1.8 }}
    />
  </motion.div>
);

// Decorative corner flourish
const Corner = ({ className, flip }) => (
  <motion.svg
    aria-hidden
    className={`h-14 w-14 sm:h-[120px] sm:w-[120px] ${className}`}
    viewBox="0 0 120 120"
    fill="none"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 0.6, scale: 1 }}
    transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 2.1 }}
    style={{ transform: flip }}
  >
    <path
      d="M8 8h34M8 8v34M8 8c40 4 68 32 72 72"
      stroke="var(--gold)"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.7"
    />
    <path d="M18 18c26 3 45 22 48 48" stroke="var(--gold)" strokeWidth="0.7" opacity="0.4" />
    <circle cx="8" cy="8" r="2" fill="var(--gold)" />
  </motion.svg>
);

export default function ComingSoon() {
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);

  // Mouse parallax for the crest
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 60, damping: 18, mass: 0.6 });
  const rotX = useTransform(sy, [-0.5, 0.5], [7, -7]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const glowX = useTransform(sx, [-0.5, 0.5], ["42%", "58%"]);
  const glowBg = useTransform(
    glowX,
    (x) =>
      `radial-gradient(60% 55% at ${x} 38%, rgba(217,178,94,0.20) 0%, rgba(254,246,233,0) 62%)`
  );

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  const handleMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <main
      ref={containerRef}
      onMouseMove={handleMove}
      data-testid="coming-soon-screen"
      className="cs-grain relative flex flex-col items-center justify-center overflow-hidden text-center"
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#fef6e9",
      }}
    >
      {/* Radial warmth following cursor */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: glowBg,
        }}
      />
      {/* Faint vignette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 120% at 50% 42%, rgba(254,246,233,0) 55%, rgba(30,53,36,0.10) 100%)",
        }}
      />

      {/* Corner flourishes */}
      <Corner className="absolute left-4 top-4 sm:left-8 sm:top-8" flip="rotate(0deg)" />
      <Corner className="absolute right-4 top-4 sm:right-8 sm:top-8" flip="scaleX(-1)" />
      <Corner className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8" flip="scaleY(-1)" />
      <Corner
        className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8"
        flip="scale(-1,-1)"
      />

      <motion.div
        className="relative z-10 flex flex-col items-center px-6"
        initial="hidden"
        animate={ready ? "show" : "hidden"}
      >
        {/* Top eyebrow */}
        <motion.p
          variants={fadeUp}
          custom={0.2}
          className="cs-serif-sc mb-6 text-[0.6rem] uppercase tracking-[0.45em] text-[var(--gold)] sm:text-xs sm:tracking-[0.55em]"
          data-testid="cs-eyebrow"
        >
          Est. Heritage of Brij
        </motion.p>

        {/* Crest / logo with float + parallax */}
        <motion.div
          style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
          className="will-change-transform"
        >
          <motion.img
            src="/brij-logo.png"
            alt="Brij Vasundhara Hotel & Resort"
            data-testid="cs-logo"
            className="mx-auto h-auto w-auto max-h-[26vh] max-w-[200px] select-none sm:max-h-[30vh] sm:max-w-[240px]"
            draggable={false}
            initial={{ opacity: 0, scale: 0.86, y: 20 }}
            animate={
              ready
                ? {
                    opacity: 1,
                    scale: 1,
                    y: [0, -10, 0],
                  }
                : {}
            }
            transition={{
              opacity: { duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.25 },
              scale: { duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.25 },
              y: {
                duration: 6,
                ease: "easeInOut",
                repeat: Infinity,
                delay: 1.6,
              },
            }}
            style={{
              filter: "drop-shadow(0 26px 40px rgba(30,53,36,0.18))",
            }}
          />
        </motion.div>

        {/* COMING SOON — masked line reveal */}
        <div className="mt-7 sm:mt-8" data-testid="cs-heading">
          <h1 className="cs-serif leading-[1.15] text-[var(--forest)]">
            <span className="cs-mask block">
              <motion.span
                variants={revealLine}
                custom={0}
                className="block text-[clamp(1.6rem,5vw,3.25rem)] tracking-[0.12em]"
              >
                Coming
              </motion.span>
            </span>
            <span className="cs-mask ml-3 block sm:ml-4">
              <motion.span
                variants={revealLine}
                custom={1}
                className="block text-[clamp(1.6rem,5vw,3.25rem)] tracking-[0.2em] text-[var(--gold)]"
              >
                Soon
              </motion.span>
            </span>
          </h1>
        </div>

        {/* Divider */}
        <div className="mt-6 sm:mt-7">
          <Divider />
        </div>

        {/* Tagline */}
        <motion.p
          variants={fadeUp}
          custom={2.05}
          data-testid="cs-tagline"
          className="cs-cormorant mt-6 max-w-xl text-balance text-lg italic leading-relaxed text-[var(--forest)]/80 sm:text-2xl"
        >
          A sanctuary of timeless hospitality is taking shape — where the soul of
          Brij meets the art of modern luxury.
        </motion.p>

        {/* Venture attribution */}
        <motion.p
          variants={fadeUp}
          custom={2.25}
          data-testid="cs-venture"
          className="cs-serif-sc mt-6 text-[0.6rem] uppercase leading-relaxed tracking-[0.25em] text-[var(--gold)] sm:text-xs sm:tracking-[0.3em]"
        >
          A Venture of Milan Tent &amp; Caterers
          <br />
          Founded by Shri Ram Babu Sharma
        </motion.p>
      </motion.div>
    </main>
  );
}
